import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  checkEmailAvailability, 
  registerUser, 
  getCountryByCode, 
  getTimezoneByCountry 
} from '../api/ssoAuth';
import { getRedirectUri, getAppName, getAppConfig } from '../utils/redirectHelper';
import { useDomain } from '../contexts/DomainContext';
import CompanyLogo from '../components/CompanyLogo';
import AuthFooter from '../components/AuthFooter';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// removed password visibility icons as password fields removed
import './Register.css';
import countryCodes from '../data/countryCodes.json';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [step, setStep] = useState(1); // 1: Email check, 2: Full registration
  const [refUrl, setRefUrl] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);
  
  const { domainConfig } = useDomain();
  const redirectUri = getRedirectUri();
  const appName = getAppName();
  const appConfig = getAppConfig();
  
  const [formData, setFormData] = useState({
    email: '',
    userName: '',
    firstName: '',
    lastName: '',
    mobile: '',
    companyName: '',
    // office/password fields removed
    countryCode: '+91',
    countryId: '',
    timezoneId: '',
  });

  const [validation, setValidation] = useState({
    emailValid: false,
    emailAvailable: false,
    mobileValid: false,
    usernameValid: false,
    // passwordMatch removed
  });

  // Capture redirect_uri from URL params on component mount, and block
  // direct access to this page when no redirect_uri was provided at all.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasRedirectUri = params.get('redirect_uri') || params.get('redirectUri');

    if (!hasRedirectUri) {
      console.warn('Direct access blocked - no redirect_uri parameter');
      setAccessBlocked(true);
      return;
    }

    const redirectUri = getRedirectUri();
    if (redirectUri) {
      setRefUrl(redirectUri);
    }
  }, []);

  // Country codes are loaded from src/data/countryCodes.json

  // Email validation function
  const validateEmail = (email) => {
    const regex = /^[A-Za-z0-9_.+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
    return regex.test(email);
  };

  // Check for unicode characters in username
  const hasUnicodeCharacters = (str) => {
    for (let i = 0; i < str.length; i++) {
      if (str.charCodeAt(i) > 127) {
        return true;
      }
    }
    return false;
  };

  // Validate username
  const validateUsername = (username) => {
    if (username.length < 5) {
      return { valid: false, message: 'Username must be at least 5 characters' };
    }
    if (hasUnicodeCharacters(username)) {
      return { valid: false, message: 'Unicode characters not allowed in username' };
    }
    if (username.includes(' ')) {
      return { valid: false, message: 'Spaces not allowed in username' };
    }
    return { valid: true, message: '' };
  };

  // Validate mobile number
  const validateMobile = (mobile, countryCode) => {
    if (!mobile) return false;
    
    const mobileLen = mobile.length;
    
    if (countryCode === '+91') {
      // Indian mobile validation
      const firstDigit = mobile.charAt(0);
      if (!['6', '7', '8', '9'].includes(firstDigit)) {
        return false;
      }
      return mobileLen === 10;
    } else {
      // International mobile validation
      return mobileLen >= 5 && mobileLen <= 12;
    }
  };


  // Handle email change and validation
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({ ...formData, email });
    
    const isValid = validateEmail(email);
    setValidation({ ...validation, emailValid: isValid, emailAvailable: false });
  };

  // Check email availability
  const handleEmailCheck = async () => {
    if (!validation.emailValid) {
      toast.error('Please enter a valid email address');
      return;
    }

    setEmailChecking(true);
    try {
      const result = await checkEmailAvailability(formData.email);
      
      if (result.available) {
        setValidation({ ...validation, emailAvailable: true });
        toast.success(result.message || 'Email is available!');
        setStep(2); // Move to full registration
      } else {
        toast.error(result.message || 'Email already exists. Please use a different email or login.');
        setValidation({ ...validation, emailAvailable: false });
      }
    } catch (error) {
      toast.error('Error checking email availability');
    } finally {
      setEmailChecking(false);
    }
  };

  // Handle username change
  const handleUsernameChange = (e) => {
    const username = e.target.value;
    setFormData({ ...formData, userName: username });
    
    const validation_result = validateUsername(username);
    setValidation({ 
      ...validation, 
      usernameValid: validation_result.valid 
    });
    
    if (!validation_result.valid && username.length > 0) {
      toast.warn(validation_result.message);
    }
  };

  // Handle mobile change and load country/timezone
  const handleMobileChange = (e) => {
    const mobile = e.target.value;
    
    // Only allow numbers
    if (mobile && isNaN(mobile)) {
      return;
    }

    // For Indian numbers, validate first digit
    if (formData.countryCode === '+91' && mobile.length === 1) {
      if (!['6', '7', '8', '9'].includes(mobile)) {
        toast.warn('Indian mobile numbers must start with 6, 7, 8, or 9');
        return;
      }
    }

    setFormData({ ...formData, mobile });
    
    const isValid = validateMobile(mobile, formData.countryCode);
    setValidation({ ...validation, mobileValid: isValid });
    
    // Auto-load country and timezone when mobile is valid
    if (isValid && !formData.countryId) {
      loadCountryAndTimezone(formData.countryCode);
    }
  };

  // Load country and timezone based on country code
  const loadCountryAndTimezone = async (countryCode) => {
    try {
      const countryData = await getCountryByCode(countryCode);
      
      if (countryData && countryData.length > 0) {
        // Normalize API response keys (Value/Text or value/text)
        const normalizedCountries = countryData.map(c => ({
          value: c.Value ?? c.value ?? c.ValueId ?? c.ValueID ?? '',
          text: c.Text ?? c.text ?? c.Name ?? ''
        }));

        const firstCountryId = normalizedCountries[0].value;
        setFormData(prev => ({ ...prev, countryId: firstCountryId }));

        // Load timezones for this country
        const timezoneData = await getTimezoneByCountry(firstCountryId);
        if (timezoneData && timezoneData.length > 0) {
          const normalizedTZ = timezoneData.map(t => ({
            value: t.Value ?? t.value ?? t.ValueId ?? t.ValueID ?? '',
            text: t.Text ?? t.text ?? t.Name ?? ''
          }));
          setFormData(prev => ({ ...prev, timezoneId: normalizedTZ[0].value }));
        }
      }
    } catch (error) {
      console.error('Error loading country/timezone:', error);
    }
  };

  // Handle selecting a country code from dropdown
  const handleCountryCodeSelect = async (e) => {
    const countryCode = e.target.value;
    setFormData({
      ...formData,
      countryCode,
      mobile: '',
      countryId: '',
      timezoneId: ''
    });
    setValidation({ ...validation, mobileValid: false });

    // load country/timezone for selected code
    await loadCountryAndTimezone(countryCode);
  };

  // Load countries/timezones for the default country code on mount
  // (no auto-loading on mount since country/timezone UI removed)

  // (country/timezone selection removed from UI)

  // password fields removed

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    if (!validation.emailValid || !validation.emailAvailable) {
      toast.error('Please verify your email first');
      return;
    }
    
    if (!validation.usernameValid) {
      toast.error('Please enter a valid username (minimum 5 characters)');
      return;
    }
    
    if (!formData.firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    
    if (!validation.mobileValid) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    
    if (!formData.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    // country/timezone selection removed from form
    
    // password/confirm password removed from registration flow

    setLoading(true);

    try {
      const result = await registerUser(formData, refUrl);
      
      if (result.success) {
        // Success: Display the message from API (e.g., "You have registered Successfully")
        toast.success(result.message);
        
        // Redirect to login after 2 seconds with preserved URL parameters
        setTimeout(() => {
          const currentSearch = window.location.search;
          navigate('/login' + currentSearch);
        }, 2000);
      } else {
        // Failure: Display error message (e.g., "Email already registered.")
        toast.error(result.message || 'Registration failed');
      }
    } catch (error) {
      // Unexpected error
      toast.error('Registration failed. Please try again.');
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Back to login
  const handleBackToLogin = () => {
    // Preserve URL parameters when navigating back to login
    const currentSearch = window.location.search;
    navigate('/login' + currentSearch);
  };

  // Back to email check
  const handleBackToEmail = () => {
    setStep(1);
    setFormData({
      ...formData,
      userName: '',
      firstName: '',
      lastName: '',
      mobile: '',
      companyName: '',
    });
  };

  if (accessBlocked) {
    return (
      <div className="sso-register-container">
        <ToastContainer position="top-right" autoClose={3000} />

        <div className="register-card access-blocked">
          <div className="register-header">
            <CompanyLogo
              appConfig={appConfig}
              domainConfig={domainConfig}
              appName={appName}
            />
            <p className="subtitle">Access Denied</p>
          </div>

          <div className="blocked-message">
            <div className="blocked-icon">🔒</div>
            <h3>Direct Access Not Allowed</h3>
            <p>This SSO registration page cannot be accessed directly.</p>
            <p>Please access this page through an authorized application.</p>
          </div>

          <AuthFooter domainConfig={domainConfig} />
        </div>
      </div>
    );
  }

  return (
    <div className="sso-register-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="register-card">
        <div className="register-header">
          <CompanyLogo
            appConfig={appConfig}
            domainConfig={domainConfig}
            appName={appName}
          />
          <p className="subtitle">
            {step === 1 ? 'Create Your Account' : 'Complete Registration'}
          </p>
        </div>

        {step === 1 ? (
          // Step 1: Email Verification
          <div className="email-check-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className={`form-control ${formData.email && (validation.emailValid ? 'valid' : 'invalid')}`}
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleEmailChange}
                autoFocus
              />
              {formData.email && !validation.emailValid && (
                <span className="error-text">Invalid email address</span>
              )}
            </div>

            <button 
              type="button"
              className="btn-primary"
              onClick={handleEmailCheck}
              disabled={!validation.emailValid || emailChecking}
            >
              Get Started
              {emailChecking && <span className="spinner" />}
            </button>

            <div className="form-footer">
              <p>
                Already have an account?{' '}
                <button 
                  type="button" 
                  className="link-button"
                  onClick={handleBackToLogin}
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        ) : (
          // Step 2: Full Registration Form
          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="form-control"
                  value={formData.email}
                  disabled
                />
              </div>
            </div>

            {/* Username & Company moved below Country Code */}
            <div className="form-row two-columns">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-control"
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-control"
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
            </div>

            

            <div className="form-row two-columns">
              <div className="form-group">
                <label htmlFor="countryCode">Country *</label>
                <select
                  id="countryCode"
                  name="countryCode"
                  className="form-control"
                  value={formData.countryCode}
                  onChange={handleCountryCodeSelect}
                  required
                >
                  {countryCodes.map(country => (
                    <option key={`${country.code}-${country.name}`} value={country.code}>
                      {`${country.code} - ${country.name}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile Number *</label>
                <input
                  type="text"
                  id="mobile"
                  name="mobile"
                  className={`form-control ${formData.mobile && (validation.mobileValid ? 'valid' : 'invalid')}`}
                  placeholder={formData.countryCode === '+91' ? '10 digits' : '5-12 digits'}
                  value={formData.mobile}
                  onChange={handleMobileChange}
                  maxLength={formData.countryCode === '+91' ? 10 : 12}
                  required
                />
              </div>
            </div>

            {/* Insert Username & Company Name below Country Code */}
            <div className="form-row two-columns">
              <div className="form-group">
                <label htmlFor="userName">Username *</label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  className={`form-control ${formData.userName && (validation.usernameValid ? 'valid' : 'invalid')}`}
                  placeholder="Enter username (min 5 characters)"
                  value={formData.userName}
                  onChange={handleUsernameChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  className="form-control"
                  placeholder="Enter company name"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Country/timezone row removed per request */}

            {/* Office and password fields removed per request */}

            <div className="form-actions">
              <button 
                type="button"
                className="btn-secondary"
                onClick={handleBackToEmail}
              >
                Back
              </button>
              
              <button 
                type="submit" 
                className="btn-primary"
                disabled={loading || !validation.mobileValid || !validation.usernameValid}
              >
                Create Account
                {loading && <span className="spinner" />}
              </button>
            </div>

            <div className="form-footer">
              <p>
                Already have an account?{' '}
                <button 
                  type="button" 
                  className="link-button"
                  onClick={handleBackToLogin}
                >
                  Sign In
                </button>
              </p>
            </div>
          </form>
        )}

        <AuthFooter 
          domainConfig={domainConfig}
        />
      </div>
    </div>
  );
};

export default Register;
